import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import subscriptionService from '../services/subscriptionService';

const SubscriptionContext = createContext({});

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const auth = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs to prevent duplicate API calls
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const intervalRef = useRef(null);
  
  // Ref to track current auth state
  const authRef = useRef(auth);
  
  useEffect(() => {
    authRef.current = auth;
  }, [auth]);

  const loadSubscription = useCallback(async (forceReload = false, isBackground = false) => {
    // Use ref to get current auth state since this callback is memoized with []
    const currentAuth = authRef.current;
    const isAuthenticated = currentAuth.isAuthenticated;
    const authToken = currentAuth.user?.access_token;

    if (!isAuthenticated || !authToken) {
      setLoading(false);
      return;
    }

    // Prevent duplicate calls unless force reload
    if (!forceReload && (isLoadingRef.current || hasLoadedRef.current)) {
      console.log('Subscription: Prevented duplicate call');
      return;
    }

    try {
      console.log(forceReload ? 'Subscription: Force reloading subscription data' : 'Subscription: Loading subscription data...');
      isLoadingRef.current = true;
      
      // Only show loading state if not a background refresh
      if (!isBackground) {
        setLoading(true);
      }
      setError(null);
      
      // If force reload AND NOT background refresh, clear cached data
      // For background refreshes, we want to keep showing the old data while fetching the new one
      if (forceReload && !isBackground) {
        setSubscription(null);
        hasLoadedRef.current = false;
      }
      
      const subscriptionData = await subscriptionService.getUserSubscription(authToken, forceReload);
      setSubscription(subscriptionData);
      hasLoadedRef.current = true;
      console.log('Subscription: Successfully loaded');
    } catch (err) {
      console.error('Error loading subscription:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load subscription on auth change - only once per authentication session
  useEffect(() => {
    const isAuthenticated = auth.isAuthenticated;
    const authToken = auth.user?.access_token;

    if (isAuthenticated && authToken && !hasLoadedRef.current) {
      loadSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user?.access_token]);

  // Reload subscription every 5 minutes to keep data fresh
  useEffect(() => {
    const isAuthenticated = auth.isAuthenticated;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (!isAuthenticated || !subscription) return; // Wait until we have loaded data

    console.log('Subscription: Setting up 5-minute refresh interval');
    intervalRef.current = setInterval(() => {
      console.log('Subscription: Interval refresh triggered');
      loadSubscription(true, true); // Force reload on interval, background refresh
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      if (intervalRef.current) {
        console.log('Subscription: Clearing refresh interval');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, subscription]);

  // Method to invalidate cache and force reload
  const invalidateCache = useCallback(async () => {
    console.log('Subscription: Invalidating cache and reloading...');
    
    // Clear service-level cache
    subscriptionService.clearCache();
    
    // Reset the loaded flag to allow fresh loading
    hasLoadedRef.current = false;
    
    return await loadSubscription(true);
  }, [loadSubscription]);

  // Method specifically for after payment processing
  const handlePaymentSuccess = useCallback(async () => {
    console.log('Payment successful, invalidating cache and reloading subscription...');
    
    // Clear session storage for trial banner to show updated state
    sessionStorage.removeItem('trialBannerDismissed');
    
    // Force reload subscription data
    return await invalidateCache();
  }, [invalidateCache]);

  const value = {
    subscription,
    loading,
    error,
    reload: loadSubscription,
    invalidateCache,
    handlePaymentSuccess,
    // Helper methods
    isTrialActive: subscription?.status === 'trial',
    isSubscriptionActive: ['active', 'trial'].includes(subscription?.status),
    daysRemaining: subscription?.subscriptionEndDate ? 
      subscriptionService.calculateDaysRemaining(subscription.subscriptionEndDate) : 0,
    isTrialExpiringSoon: subscription?.status === 'trial' && 
      subscriptionService.calculateDaysRemaining(subscription.subscriptionEndDate) <= 3
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
